import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Input,
  InputLeftElement,
  InputGroup,
  Icon,
  Flex,
  useToast,
  Skeleton,
  Badge,
  useColorModeValue,
  VStack,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { PiBuildingApartmentFill } from 'react-icons/pi';
import { FaBuilding, FaCity, FaFilter } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import baseUrl from 'Base_Url/baseUrl';

const MotionBox = motion(Box);
const MotionTr = motion(Tr);

function DepartmentUI() {
  const toast = useToast();
  const [departments, setDepartments] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [departmentName, setDepartmentName] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const token = localStorage.getItem('token');

  const inputTextColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const headerGradient = useColorModeValue(
    'linear(to-r, blue.500, teal.500)',
    'linear(to-r, blue.600, teal.600)',
  );
  const hoverBg = useColorModeValue('blue.50', 'gray.700');

  const fetchDepartments = useCallback(
    async (search = '') => {
      setFetching(true);
      try {
        const query = new URLSearchParams({ search }).toString();
        const response = await fetch(
          `${baseUrl}/master/all-department?${query}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = await response.json();
        const dataArray = data?.data?.data || [];
        dataArray.sort((a, b) => a.name.localeCompare(b.name));
        setDepartments(dataArray);
      } catch (err) {
        toast({
          title:
            err.response?.data?.message ||
            err.response?.data?.data ||
            'Error fetching departments',
          description: err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setFetching(false);
      }
    },
    [token],
  );

  useEffect(() => {
    fetchDepartments(searchInput);
  }, [searchInput, fetchDepartments]);

  const fetchDistricts = useCallback(async () => {
    try {
      const res = await fetch(`${baseUrl}/master/all-districts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok) {
        const districtsList = data?.data?.data || [];
        setDistricts(districtsList);
        if (districtsList.length > 0) {
          setSelectedDistrictId(districtsList[0]._id);
        }
      } else {
        toast({
          title: 'Failed to fetch districts',
          description: data.message || 'Something went wrong',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title:
          err.response?.data?.message ||
          err.response?.data?.data ||
          'Error fetching districts',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [token]);

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts]);

  return (
    <>
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Box
          p={{ base: 4, md: 6 }}
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.05)"
          border="1px solid"
          borderColor={borderColor}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-50px"
            right="-50px"
            w="150px"
            h="150px"
            borderRadius="full"
            bgGradient="linear(to-br, blue.100, purple.100)"
            opacity={0.1}
          />
          <Flex
            justify="space-between"
            align="center"
            mb={3}
            position="relative"
            zIndex={1}
          >
            <Flex align="center" gap={3}>
              <Flex
                align="center"
                justify="center"
                w="50px"
                h="50px"
                borderRadius="xl"
                bgGradient="linear(to-br, blue.500, purple.600)"
                boxShadow="0 8px 25px rgba(66, 153, 225, 0.3)"
              >
                <Icon as={PiBuildingApartmentFill} boxSize={6} color="white" />
              </Flex>
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  Departments
                </Text>
                <Text fontSize="sm" color={subtitleColor}>
                  Manage organizational departments
                </Text>
              </VStack>
            </Flex>

            <Badge
              colorScheme="blue"
              fontSize="xs"
              px={3}
              py={1}
              borderRadius="full"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Text>{departments.length}</Text>
              <Text>Total</Text>
            </Badge>
          </Flex>

          <Divider mb={3} />

          <Flex mb={6} position="relative" zIndex={1}>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search departments..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                borderRadius="xl"
                border="2px solid"
                color={inputTextColor}
                borderColor={borderColor}
                _focus={{
                  borderColor: 'blue.500',
                  boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.15)',
                }}
                _hover={{ borderColor: 'gray.300' }}
              />
            </InputGroup>
          </Flex>

          <Box
            borderRadius="xl"
            overflow="hidden"
            border="1px solid"
            borderColor={borderColor}
            position="relative"
            zIndex={1}
          >
            {fetching ? (
              <Box p={6}>
                <VStack spacing={3} align="stretch">
                  {[...Array(5)].map((_, i) => (
                    <Flex key={i} gap={4}>
                      <Skeleton height="40px" width="50px" borderRadius="md" />
                      <Skeleton height="40px" flex="1" borderRadius="md" />
                      <Skeleton height="40px" flex="1" borderRadius="md" />
                    </Flex>
                  ))}
                </VStack>
              </Box>
            ) : departments.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                p={10}
                minH="200px"
              >
                <Box
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  bg="gray.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={4}
                >
                  <Icon as={FaBuilding} w={8} h={8} color="gray.400" />
                </Box>
                <Text
                  fontSize="lg"
                  fontWeight="medium"
                  color={textColor}
                  mb={2}
                >
                  No departments found
                </Text>
                <Text color={subtitleColor} textAlign="center" fontSize="sm">
                  {searchInput
                    ? `No departments match "${searchInput}"`
                    : 'Start by adding your first department'}
                </Text>
              </Flex>
            ) : (
              <Box overflow="auto" height={'30vh'}>
                <Table variant="simple">
                  <Thead bg={headerGradient}>
                    <Tr>
                      <Th
                        color="gray"
                        fontSize="sm"
                        fontWeight="bold"
                        py={4}
                        textAlign="center"
                      >
                        #
                      </Th>
                      <Th color="gray" fontSize="sm" fontWeight="bold" py={4}>
                        DEPARTMENT
                      </Th>
                      <Th color="gray" fontSize="sm" fontWeight="bold" py={4}>
                        DISTRICT
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <AnimatePresence>
                      {departments.map((dept, index) => (
                        <MotionTr
                          key={dept._id || index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          _hover={{
                            bg: hoverBg,
                            transition: 'background 0.2s',
                          }}
                        >
                          <Td
                            textAlign="center"
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                            py={3}
                          >
                            <Badge
                              colorScheme="blue"
                              variant="subtle"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              {index + 1}
                            </Badge>
                          </Td>
                          <Td
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                            py={3}
                          >
                            <HStack>
                              <Icon
                                as={FaBuilding}
                                color="blue.500"
                                boxSize={4}
                              />
                              <Text>{dept?.name}</Text>
                            </HStack>
                          </Td>
                          <Td fontSize="sm" color={subtitleColor} py={3}>
                            <HStack>
                              <Icon as={FaCity} color="green.500" boxSize={4} />
                              <Text>{dept.district?.name || '—'}</Text>
                            </HStack>
                          </Td>
                        </MotionTr>
                      ))}
                    </AnimatePresence>
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </Box>
      </MotionBox>
    </>
  );
}

export default DepartmentUI;
